import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";
import { useSocket } from "../context/SocketContext";

// Item 17 — Graceful offline handling.
// A slim, non-intrusive top banner that appears when the wolf loses connectivity —
// detected via the browser's online/offline signal AND the live WebSocket state
// (so "browser online but backend unreachable" is caught too). When connectivity
// returns it shows a brief "Back online" confirmation, auto-recovers without a manual
// refresh (the WS reconnects + presence/badges re-sync), and fires `pack:reconnected`
// so data-heavy surfaces (e.g. the Howl Feed) can quietly refetch.
const WS_GRACE_MS = 8000; // ignore brief, normal WS reconnect blips

export function OfflineBanner() {
  const { connected, reconnecting, gaveUp, retryConnection } = useSocket();
  const [browserOnline, setBrowserOnline] = useState(navigator.onLine !== false);
  const [backendDown, setBackendDown] = useState(false);
  const [recovered, setRecovered] = useState(false);
  const graceTimer = useRef(null);
  const recoverTimer = useRef(null);
  const wasDegraded = useRef(false);

  useEffect(() => {
    const goOnline = () => setBrowserOnline(true);
    const goOffline = () => setBrowserOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Only flag the backend as down if the WS stays disconnected past a short grace
  // window (a normal reconnect takes ~2.5s and shouldn't alarm anyone).
  useEffect(() => {
    clearTimeout(graceTimer.current);
    if (!connected && browserOnline && (reconnecting || gaveUp)) {
      setBackendDown(true);
    } else if (!connected && browserOnline) {
      graceTimer.current = setTimeout(() => setBackendDown(true), WS_GRACE_MS);
    } else {
      setBackendDown(false);
    }
    return () => clearTimeout(graceTimer.current);
  }, [connected, browserOnline, reconnecting, gaveUp]);

  const degraded = !browserOnline || backendDown;

  // On the degraded -> healthy transition, show "Back online" briefly and let
  // surfaces refetch. Recovery is automatic (no manual refresh needed).
  useEffect(() => {
    if (degraded) {
      wasDegraded.current = true;
      setRecovered(false);
      clearTimeout(recoverTimer.current);
    } else if (wasDegraded.current) {
      wasDegraded.current = false;
      setRecovered(true);
      window.dispatchEvent(new Event("pack:reconnected"));
      recoverTimer.current = setTimeout(() => setRecovered(false), 3000);
    }
    return () => clearTimeout(recoverTimer.current);
  }, [degraded]);

  const visible = degraded || recovered;
  const bannerRef = useRef(null);
  // Push the fixed header (and everything offset by it) below the banner so nothing is covered.
  useLayoutEffect(() => {
    const root = document.documentElement.style;
    const el = bannerRef.current;
    if (!visible || !el) { root.removeProperty("--hdr-top"); return undefined; }
    const set = () => root.setProperty("--hdr-top", `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); root.removeProperty("--hdr-top"); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      data-testid={degraded ? "offline-banner" : "reconnected-banner"}
      role="status"
      aria-live="polite"
      className={`fixed top-0 inset-x-0 z-[100] flex items-center justify-center gap-2 px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] text-sm font-medium text-center transition-colors duration-300 ${
        degraded
          ? "bg-amber-950/95 text-amber-200 border-b border-amber-700/50"
          : "bg-emerald-950/95 text-emerald-200 border-b border-emerald-700/50"
      }`}
      style={{ backdropFilter: "blur(8px)" }}
    >
      {degraded ? (
        <>
          <WifiOff className={`w-4 h-4 shrink-0 ${gaveUp ? "" : "animate-pulse"}`} />
          {!browserOnline ? (
            <span>You're offline — trying to restore the connection…</span>
          ) : (
            <button type="button" onClick={retryConnection} data-testid="ws-reconnecting-indicator" className="hover:underline underline-offset-2">
              <span data-testid="ws-reconnecting-text">{gaveUp ? "Connection lost · tap to retry" : "Reconnecting… tap to retry"}</span>
            </button>
          )}
        </>
      ) : (
        <>
          <Wifi className="w-4 h-4 shrink-0" />
          <span>Back online — the pack is with you again.</span>
        </>
      )}
    </div>
  );
}
